'use client'

import CommentBlockTextarea from '@/app/[locale]/[roomId]/components/CommentBlockTextarea'
import CommentLikeButton from '@/app/[locale]/[roomId]/components/CommentLikeButton'
import CommentMoreActionsButton from '@/app/[locale]/[roomId]/components/CommentMoreActionsButton'
import CommentTitle from '@/app/[locale]/[roomId]/components/CommentTitle'
import DeleteCommentConfirmModal from '@/app/[locale]/[roomId]/components/DeleteCommentConfirmModal'
import DeleteThreadConfirmModal from '@/app/[locale]/[roomId]/components/DeleteThreadConfirmModal'
import SignInConfirmModal from '@/app/[locale]/[roomId]/components/SignInConfirmModal'
import IconBack from '@/components/assets/icon_back.svg'
import ReadMoreText from '@/components/ReadMoreText'
import ReportConfirmationModal from '@/components/ReportConfirmationModal'
import ReportModal from '@/components/ReportModal'
import { LoadingDots } from '@/components/ui/LoadingDots'
import ViverseIconButton from '@/components/ViverseIconButton'
import { ReportReason } from '@/constants/comment'
import { useAuthContext } from '@/context/AuthContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { cn } from '@/lib/utils'
import { Comment } from '@/types/comments'
import { useTranslations } from 'next-intl'
import { ReactNode, useEffect, useState } from 'react'
import { useIsClient, useMediaQuery } from 'usehooks-ts'

type CommentBlockProps = {
  comment: Comment
  isReply: boolean
  addComment: (value: string) => Promise<void>
  removeComment: (commentId: string) => void
  updateCommentLikeCount: (commentId: string, isLiked: boolean) => void
  reportComment: (commentId: string, reason: ReportReason, detail: string) => void
  children?: ReactNode
}

function CommentBlock({
  comment,
  isReply,
  addComment,
  removeComment,
  updateCommentLikeCount,
  reportComment,
  children,
}: CommentBlockProps) {
  const t = useTranslations()
  const isTablet = useMediaQuery('(min-width: 768px)')
  const { store } = useConfigsContext()
  const { isSignedIn } = useAuthContext()
  const isClient = useIsClient()

  const [isLiked, setIsLiked] = useState(comment.is_liked || false)
  const [likeCount, setLikeCount] = useState(comment.like_count || 0)
  const [isReplying, setIsReplying] = useState(false)
  const [isAddingComment, setIsAddingComment] = useState(false)
  const [showSignInModal, setShowSignInModal] = useState(false)
  const [showRemoveCommentModal, setShowRemoveCommentModal] = useState(false)
  const [showReportConfirmationModal, setShowReportConfirmationModal] = useState(false)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)

  const isRemovedByUser = comment.is_reported
  const isRemovedByMod = comment.delete_by_mod
  const isRemoved = isRemovedByUser || isRemovedByMod

  const commentContent = isRemovedByMod
    ? t('comment-block.removed-by-mod')
    : isRemovedByUser
      ? t('comment-block.removed-by-user')
      : comment.content

  const handleAddComment = async (value: string) => {
    if (isSignedIn) {
      setIsAddingComment(true)
      await addComment(value)
      setIsReplying(false)
      setIsAddingComment(false)
    } else {
      setShowSignInModal(true)
    }
  }

  const handleClickReply = () => {
    if (isSignedIn) {
      setIsReplying((prev) => !prev)
    } else {
      setShowSignInModal(true)
    }
  }

  const handleClickLike = async () => {
    const htcToken = store?.credentials?.htcToken
    if (!(isSignedIn && htcToken)) {
      setShowSignInModal(true)
      return
    }

    try {
      setIsLiked((prev) => !prev)
      setLikeCount((prev) => prev + (isLiked ? -1 : 1))
      await updateCommentLikeCount(comment.id, !isLiked)
    } catch (err) {
      setIsLiked((prev) => !prev)
      setLikeCount((prev) => prev - (isLiked ? -1 : 1))
      console.error('Failed to update like count:', err)
    }
  }

  const handleReportSubmit = (commentId: string, reason: ReportReason, detail: string) => {
    reportComment(commentId, reason, detail)
    setShowReportConfirmationModal(true)
  }

  useEffect(() => {
    setIsLiked(comment.is_liked || false)
    setLikeCount(comment.like_count)
  }, [comment.is_liked, comment.like_count])

  return (
    <>
      <div className="flex h-fit w-full flex-col">
        <CommentTitle isTablet={isClient && isTablet} isRemoved={isRemoved} comment={comment}>
          <CommentMoreActionsButton
            comment={comment}
            handleDelete={() => setShowRemoveCommentModal(true)}
            handleReport={() => setIsReportModalOpen(true)}
          />
        </CommentTitle>
        <div className="flex h-fit w-full flex-col gap-2 pl-10.5 md:pl-16">
          <ReadMoreText
            content={commentContent}
            className={cn(
              'body1-omni-styles text-vive-white mr-12',
              isRemoved && 'text-vive-primary-60',
            )}
          />
          {!isRemoved && (
            <div className="flex items-center gap-1">
              <CommentLikeButton
                isLiked={isLiked}
                handleClick={handleClickLike}
                likeCount={likeCount}
              />
              <ViverseIconButton
                size="custom"
                variant="empty"
                className={cn(
                  'buttonS-omni-styles h-8.5 w-fit gap-1 px-2 py-1',
                  isReplying && 'bg-rgba(110,124,142,0.15) text-vive-primary-40',
                )}
                labelClassName={cn(
                  'group-hover:text-vive-white group-active:text-vive-primary-40',
                  isReplying && 'text-vive-primary-40',
                )}
                label={t('comment-block.reply')}
                onClick={handleClickReply}
              >
                <IconBack
                  className={cn(
                    '[&>path]:fill-vive-primary-20 [&>path]:group-hover:fill-vive-white [&>path]:group-active:fill-vive-primary-40 size-5',
                    isReplying && '[&>path]:fill-vive-primary-40',
                  )}
                />
              </ViverseIconButton>
            </div>
          )}
          {isReplying && !isRemoved && (
            <CommentBlockTextarea textAreaElementId={comment.id} addComment={handleAddComment} />
          )}
          {isReply ? (
            <DeleteCommentConfirmModal
              open={showRemoveCommentModal}
              onAction={() => {
                removeComment(comment.id)
                setShowRemoveCommentModal(false)
              }}
              onCancel={() => {
                setShowRemoveCommentModal(false)
              }}
            />
          ) : (
            <DeleteThreadConfirmModal
              open={showRemoveCommentModal}
              onAction={() => {
                removeComment(comment.id)
                setShowRemoveCommentModal(false)
              }}
              onCancel={() => {
                setShowRemoveCommentModal(false)
              }}
            />
          )}
          <SignInConfirmModal open={showSignInModal} onClose={() => setShowSignInModal(false)} />
          <ReportModal
            commentId={comment.id}
            isDialogOpen={isReportModalOpen}
            onSubmit={handleReportSubmit}
            onClose={() => setIsReportModalOpen(false)}
          />
          <ReportConfirmationModal
            open={showReportConfirmationModal}
            onAction={() => setShowReportConfirmationModal(false)}
          />
        </div>
        {isAddingComment && <LoadingDots size="md" />}
        {children}
      </div>
    </>
  )
}

export default CommentBlock
